<script>
import StoreHero from '@school/components/StoreHero.vue';
import TrainingsList from '@school/components/TrainingsList.vue';
import TeacherSection from '@school/components/TeacherSection.vue';
import BundlesList from '@school/components/BundlesList.vue';
import MembershipsList from '@school/components/MembershipsList.vue';
import { isEqual } from 'lodash';

const DEFAULT_SEARCH_FORM_DATA = {
  q: '',
  sort_by: '',
  order_by: 'desc',
};

export default {
  name: 'Home',
  components: {
    StoreHero,
    TrainingsList,
    TeacherSection,
    BundlesList,
    MembershipsList,
  },
  head() {
    return {
      title: this.store.name,
      titleTemplate: null,
    };
  },
  data: () => ({
    searchFormData: {
      q: '',
      sort_by: '',
      order_by: 'desc',
    },
  }),
  computed: {
    searchParamsDirty() {
      return !isEqual(DEFAULT_SEARCH_FORM_DATA, this.searchFormData);
    },
    store() {
      return this.$store.state.store.data;
    },
    filteredTrainings() {
      let value = [...this.store.trainings];

      if (this.searchFormData.q) {
        value = value
          .filter((training) => training.name?.toLowerCase().includes(this.searchFormData.q.toLowerCase())
            || training.description?.toLowerCase().includes(this.searchFormData.q.toLowerCase())
            || training.summary?.toLowerCase().includes(this.searchFormData.q.toLowerCase()));
      }

      if (this.searchFormData.sort_by === 'price') {
        value = value.sort((a, b) => {
          const aPrice = a.discount_price || a.price;
          const bPrice = b.discount_price || b.price;

          if (this.searchFormData.order_by === 'desc') {
            return bPrice - aPrice;
          }

          return aPrice - bPrice;
        });
      }

      if (this.searchFormData.sort_by === 'name') {
        value = value.sort((a, b) => {
          if (this.searchFormData.order_by === 'desc') {
            return b.name.localeCompare(a.name);
          }
          return a.name.localeCompare(b.name);
        });
      }

      return value;
    },
    filteredBundles() {
      let value = [...this.store.bundles];

      if (this.searchFormData.q) {
        value = value
          .filter((bundle) => bundle.name?.toLowerCase().includes(this.searchFormData.q.toLowerCase())
            || bundle.description?.toLowerCase().includes(this.searchFormData.q.toLowerCase())
            || bundle.summary?.toLowerCase().includes(this.searchFormData.q.toLowerCase()));
      }

      if (this.searchFormData.sort_by === 'price') {
        value = value.sort((a, b) => {
          const aPrice = a.discount_price || a.price;
          const bPrice = b.discount_price || b.price;

          if (this.searchFormData.order_by === 'desc') {
            return bPrice - aPrice;
          }

          return aPrice - bPrice;
        });
      }

      if (this.searchFormData.sort_by === 'name') {
        value = value.sort((a, b) => {
          if (this.searchFormData.order_by === 'desc') {
            return b.name.localeCompare(a.name);
          }
          return a.name.localeCompare(b.name);
        });
      }

      return value;
    },
    filteredMemberships() {
      let value = [...(this.store.memberships || [])];

      if (this.searchFormData.q) {
        value = value
          .filter((membership) => membership.name?.toLowerCase().includes(this.searchFormData.q.toLowerCase())
            || membership.summary?.toLowerCase().includes(this.searchFormData.q.toLowerCase()));
      }

      if (this.searchFormData.sort_by === 'price') {
        value = value.sort((a, b) => {
          const aPrice = a.prices && a.prices.length > 0 ? Math.min(...a.prices.map((p) => p.amount)) : 0;
          const bPrice = b.prices && b.prices.length > 0 ? Math.min(...b.prices.map((p) => p.amount)) : 0;

          if (this.searchFormData.order_by === 'desc') {
            return bPrice - aPrice;
          }
          return aPrice - bPrice;
        });
      }

      if (this.searchFormData.sort_by === 'name') {
        value = value.sort((a, b) => {
          if (this.searchFormData.order_by === 'desc') {
            return b.name.localeCompare(a.name);
          }
          return a.name.localeCompare(b.name);
        });
      }

      return value;
    },
  },
  methods: {
    resetSearchParams() {
      return this.searchFormData = { ...DEFAULT_SEARCH_FORM_DATA };
    },
    toggleOrder() {
      this.searchFormData.order_by = this.searchFormData.order_by !== 'desc' ? 'desc' : 'asc';
    },
  },
  i18n: {
    messages: {
      fr: {
        filter_by: 'Filtrer par',
        filter_by_name_or_description: 'Filtrer par nom ou description',
        sort_by: 'Trier par',
        default: 'Par défaut',
        name: 'Nom',
        price: 'Prix',
        ascending: 'croissant',
        descending: 'décroissant',
        reset_search_parameters: 'Réinitialiser les paramètres de recherche',
        packs: 'Packs',
        no_trainings_yet: 'Il n\'y a pas encore de formations !',
        no_trainings_match_search: 'Aucune formation ne correspond à votre recherche.',
        no_bundles_yet: 'Il n\'y a pas encore de packs !',
        no_bundles_match_search: 'Aucun pack ne correspond à votre recherche.',
      },
      en: {
        filter_by: 'Filter by',
        filter_by_name_or_description: 'Filter by name or description',
        sort_by: 'Sort by',
        default: 'Default',
        name: 'Name',
        price: 'Price',
        ascending: 'ascending',
        descending: 'descending',
        reset_search_parameters: 'Reset search parameters',
        packs: 'Packs',
        no_trainings_yet: 'There are no trainings yet!',
        no_trainings_match_search: 'No trainings match your search.',
        no_bundles_yet: 'There are no packs yet!',
        no_bundles_match_search: 'No packs match your search.',
      },
    },
  },
};
</script>

<template>
  <div>
    <div class="has-background-white">
      <section class="hero is-light is-custom is-medium">
        <div class="hero-body mnh-520 py-20 is-flex is-align-items-center">
          <StoreHero class="flex-none" :store="store" />
        </div>
      </section>
    </div>

    <div class="container">
      <div class="section">
        <div v-if="store.feature_options.pages.research.enabled">
          <div class="columns">
            <div class="column">
              <b-field :label="$t('filter_by')">
                <b-input v-model="searchFormData.q" :placeholder="$t('filter_by_name_or_description')" />
              </b-field>
            </div>

            <div class="column is-narrow">
              <b-field :label="$t('sort_by')">
                <b-select v-model="searchFormData.sort_by">
                  <option value="" disabled>{{ $t('default') }}</option>
                  <option value="name">{{ $t('name') }}</option>
                  <option value="price">{{ $t('price') }}</option>
                </b-select>
                <p class="control">
                  <b-tooltip :label="searchFormData.order_by == 'asc' ? $t('ascending') : $t('descending')">
                    <b-button :icon-left="searchFormData.order_by == 'asc' ? 'arrow-up' : 'arrow-down'"
                      @click="toggleOrder" />
                  </b-tooltip>
                </p>
              </b-field>
            </div>
          </div>

          <a v-if="searchParamsDirty" href="#" class="is-size-8 level-right mb-6" @click.prevent="resetSearchParams"
            style="cursor:pointer;">
            <strong>{{ $t('reset_search_parameters') }}</strong>
          </a>
        </div>

        <div v-if="store.memberships && store.memberships.length">
          <MembershipsList class="mt-5 is-medium" :memberships="filteredMemberships" :total="store.memberships.length" />
        </div>

        <div v-if="store.bundles.length">
          <BundlesList class="mt-5 is-medium" :bundles="filteredBundles" :total="store.bundles.length" />
        </div>

        <TrainingsList class="mt-5 is-medium" :trainings="filteredTrainings" :total="store.trainings.length" />
      </div>
    </div>

    <TeacherSection />
  </div>
</template>
